import { useState } from "react"
import { router } from "@inertiajs/react"
import {
  FileText,
  Image as ImageIcon,
  Type,
  Folder,
  ChevronRight,
} from "lucide-react"

import StatusBadge from "./StatusBadge"
import { AvatarStack } from "./Avatar"
import ProgressCell from "./ProgressCell"
import { cn } from "@/lib/utils"

// Category glyph per order type (neutral, per DECISIONS — no per-type color).
const TYPE_ICON = {
  manuscript: FileText,
  cover: ImageIcon,
  illustration: ImageIcon,
  formatting: Type,
  bundle: Folder,
}

// Row → the React order detail (Atrium Stage 3) via an Inertia client visit.
function goToOrder(href) {
  if (href) router.visit(href)
}

// P8-H4 "Cardstock" — below 860 the table collapses to stacked cards
// (design/spec/pm/projects-list.html @media max-width:860). Breakpoints follow
// the Drawbridge convention: v4 compiles `max-[860px]` to `@media (width <
// 860px)`, so the design's "≤860" reads as "below 860" here. The <tr> becomes
// the card box and every cell is placed by grid-area; desktop (≥1200) keeps
// every class it already had.
const CARD_ROW =
  "max-[860px]:mb-2.5 max-[860px]:grid max-[860px]:grid-cols-[1fr_auto] max-[860px]:items-center max-[860px]:gap-[9px_10px] max-[860px]:rounded-[13px] max-[860px]:border max-[860px]:p-[13px_14px]"

// Cards carry their own surface, so the desktop row hover tint switches off.
const CARD_ROW_TOP = `${CARD_ROW} max-[860px]:bg-card max-[860px]:hover:bg-card`
// Bundle children keep the accent surface and indent as their own card.
const CARD_ROW_CHILD = `${CARD_ROW} max-[860px]:ml-5 max-[860px]:hover:bg-accent`

const CARD_CELL = "max-[860px]:block max-[860px]:p-0"
// Order date + Package leave the layout from 1199 down — the tablet band drops
// them outright, the card mode never had room for them.
const BAND_HIDDEN = "max-[1199px]:hidden"

function TitleCell({ row, indented = false, caret = null }) {
  const Icon = TYPE_ICON[row.type] ?? FileText

  return (
    <div
      className={cn(
        "flex items-center gap-[13px]",
        // The child card's own ml-5 is the indent below 860, so the desktop
        // first-cell inset drops there (design `.sub td:first-child`).
        indented && "pl-[30px] max-[860px]:pl-0"
      )}
    >
      {caret}
      <span className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] border border-border bg-secondary">
        <Icon className="size-[18px] text-muted-foreground" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <h3 className="truncate font-display text-sm font-semibold tracking-[-0.1px] max-[860px]:whitespace-normal">
          {row.title}
        </h3>
        <div className="mt-0.5 text-[11.5px] text-ink-3">{row.subtitle}</div>
      </div>
    </div>
  )
}

function RequestsBadge({ count, onReview }) {
  if (count > 0) {
    return (
      <button
        type="button"
        aria-label={`Review ${count} freelancer request${count === 1 ? "" : "s"}`}
        onClick={(event) => {
          event.stopPropagation()
          onReview?.()
        }}
        className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-primary/12 px-1.5 text-[11px] font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
      >
        {count}
      </button>
    )
  }

  return (
    <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-secondary px-1.5 text-[11px] font-bold text-ink-3">
      0
    </span>
  )
}

function GoArrow({ expandable = false, expanded = false }) {
  return (
    <span className="flex size-[30px] items-center justify-center rounded-lg border border-border text-ink-3">
      <ChevronRight
        className={cn(
          "size-[15px] transition-transform",
          expandable && (expanded ? "rotate-90" : "rotate-0"),
          !expandable && "translate-x-px"
        )}
        strokeWidth={2}
      />
    </span>
  )
}

// The status/date/package/progress/team/requests cells, shared by every row
// variant (single, bundle parent, bundle child).
function DataCells({ row, onReviewRequests }) {
  return (
    <>
      <td className={`px-4 py-[15px] ${CARD_CELL} max-[860px]:[grid-area:2/1]`}>
        <StatusBadge
          tier={row.status.tier}
          phase={row.status.phase}
          label={row.status.label}
          attention={row.status.attention}
        />
      </td>
      <td className={`px-4 py-[15px] text-[12.5px] text-muted-foreground ${BAND_HIDDEN}`}>
        {row.orderDate}
      </td>
      <td className={`px-4 py-[15px] text-[12.5px] font-medium text-foreground ${BAND_HIDDEN}`}>
        {row.package}
      </td>
      <td className={`px-4 py-[15px] ${CARD_CELL} max-[860px]:[grid-area:3/1/4/3]`}>
        <ProgressCell value={row.progress.value} label={row.progress.label} />
      </td>
      <td className={`px-4 py-[15px] ${CARD_CELL} max-[860px]:[grid-area:4/1]`}>
        <AvatarStack people={row.team} />
      </td>
      <td
        className={`px-4 py-[15px] text-center ${CARD_CELL} max-[860px]:[grid-area:2/2] max-[860px]:justify-self-end`}
      >
        <RequestsBadge count={row.requests} onReview={() => onReviewRequests?.(row)} />
      </td>
    </>
  )
}

const TITLE_CELL = `px-4 py-[15px] ${CARD_CELL} max-[860px]:[grid-area:1/1/2/3]`
const ARROW_CELL = `px-4 py-[15px] ${CARD_CELL} max-[860px]:[grid-area:4/2] max-[860px]:justify-self-end`

export default function ProjectRow({ project, onReviewRequests }) {
  const [open, setOpen] = useState(false)
  const isBundle = Array.isArray(project.children) && project.children.length > 0

  if (!isBundle) {
    return (
      <tr
        onClick={() => goToOrder(project.href)}
        className={`cursor-pointer border-t border-border transition-colors hover:bg-accent ${CARD_ROW_TOP}`}
      >
        <td className={TITLE_CELL}>
          <TitleCell row={project} />
        </td>
        <DataCells row={project} onReviewRequests={onReviewRequests} />
        <td className={ARROW_CELL}>
          <GoArrow />
        </td>
      </tr>
    )
  }

  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`cursor-pointer border-t border-border transition-colors hover:bg-accent ${CARD_ROW_TOP}`}
      >
        <td className={TITLE_CELL}>
          <TitleCell
            row={project}
            caret={
              <ChevronRight
                className={cn(
                  "size-[15px] shrink-0 text-ink-3 transition-transform",
                  open && "rotate-90"
                )}
                strokeWidth={2.5}
              />
            }
          />
        </td>
        <DataCells row={project} onReviewRequests={onReviewRequests} />
        <td className={ARROW_CELL}>
          <GoArrow expandable expanded={open} />
        </td>
      </tr>

      {open &&
        project.children.map((child) => (
          <tr
            key={child.id}
            onClick={() => goToOrder(child.href)}
            className={`cursor-pointer border-t border-border bg-accent transition-colors hover:bg-secondary ${CARD_ROW_CHILD}`}
          >
            <td className={TITLE_CELL}>
              <TitleCell row={child} indented />
            </td>
            <DataCells row={child} onReviewRequests={onReviewRequests} />
            <td className={ARROW_CELL}>
              <GoArrow />
            </td>
          </tr>
        ))}
    </>
  )
}
